import { useAuthState } from '@/app/providers/auth'
import { VendorMarketplaceOrderByProperties, VendorOrderDirection } from '@/generated/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SortIcon from '@mui/icons-material/Sort'
import { Box, Button, Menu, MenuItem, Typography, useMediaQuery } from '@mui/material'

import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VendorSearchState } from '../../../states/useVendorSearchState'

type SortOption = {
  value: string
  label: string
  data: { property: string; direction: string }
}

const BASE_OPTIONS: SortOption[] = [
  { value: 'distance-asc', label: 'Distance', data: { property: 'distance', direction: 'asc' } },
  {
    value: 'alphabet-asc',
    label: 'Alphabetical (a-z)',
    data: { property: 'alphabetical', direction: 'asc' },
  },
  {
    value: 'alphabet-desc',
    label: 'Alphabetical (z-a)',
    data: { property: 'alphabetical', direction: 'desc' },
  },
  { value: 'price-asc', label: 'Price (low-high)', data: { property: 'price', direction: 'asc' } },
  {
    value: 'price-desc',
    label: 'Price (high-low)',
    data: { property: 'price', direction: 'desc' },
  },
]

const addFavouriteSortOption = (options: SortOption[], authType: string) => {
  if (authType === 'authenticated') {
    const newOption = {
      value: 'favourites-asc',
      label: 'Favourites first',
      data: { property: 'favourites', direction: 'asc' },
    }

    const exists = options.some((option) => option.value === newOption.value)

    if (!exists) {
      options.splice(3, 0, newOption)
    }
  }
}

const addNewSortOptions = (options: SortOption[], showNewSortOptions: boolean) => {
  if (!showNewSortOptions) {
    return
  }

  const newOptions = [
    {
      value: 'deliveryfee-asc',
      label: 'Delivery Fee (low-high)',
      data: { property: 'deliveryFee', direction: 'asc' },
    },
    {
      value: 'minordervalue-asc',
      label: 'Minimum Spend (low-high)',
      data: { property: 'minOrderValue', direction: 'asc' },
    },
  ]

  const exists = options.some((option) => option.value === newOptions[0].value)

  if (!exists) {
    options.push(...newOptions)
  }
}

type Props = {
  filters: VendorSearchState['filters']
  search: VendorSearchState['search']
}

const VendorFilterSort: React.FC<Props> = ({ search, filters }) => {
  const authState = useAuthState()
  const { t } = useTranslation()
  const showNewMarketplaceSortOptions = useFeatureFlag(SPLITS.VENDOR_NEWMARKETPLACE_SORT)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isMobileScreen = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const isMediumAndBelow = useMediaQuery((theme) => theme.breakpoints.down('lg'))

  const options = useMemo(() => {
    const base = [...BASE_OPTIONS]
    addFavouriteSortOption(base, authState.type)
    addNewSortOptions(base, showNewMarketplaceSortOptions)

    return base
  }, [authState.type, showNewMarketplaceSortOptions])

  const setSort = (property: string, direction = 'asc') => {
    void search({
      orderBy: {
        property: property as VendorMarketplaceOrderByProperties,
        direction: direction as VendorOrderDirection,
      },
    })
  }

  const value =
    options.find(
      (option) =>
        option.data.property === filters.orderBy?.property &&
        option.data.direction === filters.orderBy?.direction,
    )?.value || options[0].value

  const selectedOption = options.find((option) => option.value === value) ?? options[0]

  const selectedLabel = useMemo(() => {
    let optionLabel = selectedOption.label
    const translatedOptionLabel = t(`marketplace:${selectedOption.value}`)

    if (translatedOptionLabel !== selectedOption.value) {
      optionLabel = translatedOptionLabel
    }

    return optionLabel
  }, [selectedOption, t])

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = (option: SortOption) => {
    setSort(option.data.property, option.data.direction)
    setAnchorEl(null)
  }

  const buttonId = 'supplier-sort-button'
  const menuId = 'supplier-sort-menu'

  return (
    <Box>
      <Button
        id={buttonId}
        variant="contained"
        color="inverted"
        fullWidth
        onClick={handleOpen}
        startIcon={
          <Box display="flex" alignItems="center" gap={1}>
            <SortIcon fontSize="small" />{' '}
            {!isMediumAndBelow && (
              <Typography variant="body2">{t('marketplace:sortBy', 'Sort By')}</Typography>
            )}
          </Box>
        }
        endIcon={<KeyboardArrowDownIcon />}
        aria-controls={anchorEl ? menuId : undefined}
        aria-haspopup="listbox"
        aria-expanded={anchorEl ? 'true' : undefined}
        sx={{
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          textTransform: 'none',
        }}>
        {!isMobileScreen && (
          <Typography variant="body2" fontWeight={600}>
            {selectedLabel}
          </Typography>
        )}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              mt: 1,
            },
          },

          list: {
            'aria-labelledby': buttonId,
            role: 'listbox',
            sx: {
              py: 1,
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        {options.map((option) => {
          let optionLabel = option.label
          const translatedOptionLabel = t(`marketplace:${option.value}`)
          if (translatedOptionLabel !== option.value) {
            optionLabel = translatedOptionLabel
          }

          const isSelected = option.value === value

          return (
            <MenuItem
              key={option.value}
              selected={isSelected}
              onClick={() => handleSelect(option)}
              value={option.value}
              sx={{
                borderRadius: '12px !important',
                mx: 1,
                my: 0.5,
                '&.Mui-selected': {
                  borderRadius: '12px !important',
                },
                '&:hover': {
                  borderRadius: '12px !important',
                },
              }}>
              {optionLabel}
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}

export default VendorFilterSort
